import { clsx } from 'clsx';
import { useMasterDocumentTranslation } from './i18n';
import type { MasterDocumentT } from './i18n';
import type { StatusHistoryEntry } from './adapter/MasterDocumentAdapter.types';

// ════════════════════════════════════════════════════════════
// PROPS
// ════════════════════════════════════════════════════════════

export interface FactBoxStatusHistoryProps {
  /** Entradas de historial — el componente las re-ordena por fecha DESC. */
  entries: StatusHistoryEntry[];
  /** Formateador de fecha del adapter (locale-aware). Si no se provee, muestra tiempo relativo. */
  formatDate?: (value: string) => string;
  className?: string;
}

// ════════════════════════════════════════════════════════════
// ACTION KEY → TRANSLATION KEY MAP
// Gap: C7 aún no está implementado. El backend devuelve action_key (string
// crudo). Este diccionario local los resuelve a claves de `master_document.
// statusHistory.action.*`, traducidas en el punto de render con `t()`.
// Cuando el backend devuelva processName resuelto, ese campo toma prioridad.
// ════════════════════════════════════════════════════════════

const ACTION_KEY_TRANSLATION_KEYS: Record<string, string> = {
  // Operaciones base (DocumentCore)
  create:              'master_document.statusHistory.action.create',
  save:                'master_document.statusHistory.action.save',
  approve:             'master_document.statusHistory.action.approve',
  reject:              'master_document.statusHistory.action.reject',
  cancel:              'master_document.statusHistory.action.void',
  print:               'master_document.statusHistory.action.print',

  // Transiciones de estado
  send_to_approval:    'master_document.statusHistory.action.sendToApproval',
  hold:                'master_document.statusHistory.action.hold',
  release:             'master_document.statusHistory.action.release',
  commit:              'master_document.statusHistory.action.commit',
  dispatch:            'master_document.statusHistory.action.dispatch',
  post:                'master_document.statusHistory.action.post',
  invoice:             'master_document.statusHistory.action.invoice',
  complete:            'master_document.statusHistory.action.fulfill',
  partial_fulfill:     'master_document.statusHistory.action.partialFulfillment',

  // Interfaces de capacidad (IContabilizar, IComprometer, IAnticipo…)
  contabilizar:        'master_document.statusHistory.action.post',
  comprometer:         'master_document.statusHistory.action.commit',
  anticipo:            'master_document.statusHistory.action.advance',
  recaudo:             'master_document.statusHistory.action.collection',
  cumplir:             'master_document.statusHistory.action.fulfill',
  anular:              'master_document.statusHistory.action.void',
};

function resolveProcessLabel(entry: StatusHistoryEntry, t: MasterDocumentT): string | undefined {
  if (entry.processName) return entry.processName;
  if (entry.actionKey) {
    const translationKey = ACTION_KEY_TRANSLATION_KEYS[entry.actionKey];
    return translationKey ? t(translationKey) : entry.actionKey;
  }
  return undefined;
}

// ════════════════════════════════════════════════════════════
// HELPERS
// ════════════════════════════════════════════════════════════

// El endpoint siempre resuelve changedByName (contrato explícito del backend).
// No se detectan UUIDs — el componente simplemente usa changedByName ?? changedBy.
function displayUser(changedBy: string, changedByName?: string): string {
  return changedByName ?? changedBy;
}

function relativeTime(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'hace un momento';
  if (mins < 60) return `hace ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `hace ${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `hace ${days}d`;
  const months = Math.floor(days / 30);
  return `hace ${months} mes${months > 1 ? 'es' : ''}`;
}

function formatTimestamp(isoString: string, formatDate?: (v: string) => string): string {
  if (formatDate) return formatDate(isoString);
  return relativeTime(isoString);
}

// ════════════════════════════════════════════════════════════
// COMPONENT
// ════════════════════════════════════════════════════════════

/**
 * FactBoxStatusHistory — timeline vertical del historial de transiciones del documento.
 *
 * Muestra cada entrada de `document_status_history` (OP1) con:
 * - Transición de estado: de → a (o "Creación" para la entrada inicial)
 * - Nombre del proceso: resuelto desde `processName`, o desde `actionKey`
 *   via diccionario local (gap temporal hasta que C7 esté implementado)
 * - Usuario: usa `changedByName` (el endpoint siempre lo resuelve, ej: "Sistema"
 *   mientras no haya auth real). Fallback a `changedBy` si ausente.
 * - Timestamp relativo o formateado via `adapter.formatDate`
 * - Notas opcionales registradas durante la transición
 *
 * Los registros se muestran en orden cronológico inverso (más reciente primero).
 *
 * @see IMasterDocumentAdapter.getStatusHistory — endpoint que alimenta este componente
 * @see document_status_history (OP1) — tabla append-only del backend
 *
 * @example
 * ```tsx
 * <FactBoxStatusHistory
 *   entries={historyEntries}
 *   formatDate={adapter.formatDate}
 * />
 * ```
 */
export function FactBoxStatusHistory({ entries, formatDate, className }: FactBoxStatusHistoryProps) {
  const { t } = useMasterDocumentTranslation();
  if (!entries || entries.length === 0) {
    return (
      <p className="text-xs text-content-tertiary dark:text-dark-content-secondary italic py-1">
        {t('master_document.statusHistory.empty')}
      </p>
    );
  }

  // Más reciente primero
  const sorted = [...entries].sort(
    (a, b) => new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime(),
  );

  return (
    <div className={clsx('flex flex-col', className)}>
      {sorted.map((entry, idx) => {
        const processLabel = resolveProcessLabel(entry, t);
        const userLabel = displayUser(entry.changedBy, entry.changedByName);

        return (
          <div key={entry.id} className="relative flex gap-2.5 pb-3 last:pb-0">

            {/* Línea vertical de la timeline */}
            {idx < sorted.length - 1 && (
              <div
                aria-hidden="true"
                className="absolute left-[5px] top-[14px] bottom-0 w-px bg-border-secondary dark:bg-dark-border-primary"
              />
            )}

            {/* Punto de la timeline — relleno en el más reciente */}
            <div
              aria-hidden="true"
              className={clsx(
                'relative mt-[5px] flex-shrink-0 w-[11px] h-[11px] rounded-full border-2',
                idx === 0
                  ? 'border-primary-500 dark:border-primary-400 bg-primary-500 dark:bg-primary-400'
                  : 'border-primary-400 dark:border-primary-600 bg-background-primary dark:bg-dark-bg-primary',
              )}
            />

            {/* Contenido */}
            <div className="flex-1 min-w-0">

              {/* Transición de estado */}
              <div className="flex items-center gap-1 flex-wrap leading-tight">
                {entry.previousState ? (
                  <>
                    <span className="text-[11px] font-medium text-content-secondary dark:text-dark-content-secondary truncate">
                      {entry.previousStateLabel ?? entry.previousState}
                    </span>
                    <span
                      className="text-[11px] text-content-tertiary dark:text-dark-content-secondary flex-shrink-0"
                      aria-hidden="true"
                    >
                      →
                    </span>
                  </>
                ) : (
                  <span className="text-[11px] font-medium text-content-tertiary dark:text-dark-content-secondary">
                    {t('master_document.statusHistory.creation')}
                  </span>
                )}
                <span className="text-[11px] font-semibold text-content-primary dark:text-dark-content-primary truncate">
                  {entry.newStateLabel ?? entry.newState}
                </span>
              </div>

              {/* Nombre del proceso (C7) */}
              {processLabel && (
                <div className="text-[10px] text-content-tertiary dark:text-dark-content-secondary truncate mt-0.5">
                  {processLabel}
                </div>
              )}

              {/* Usuario · timestamp */}
              <div className="flex items-center gap-1 mt-0.5">
                <span className="text-[10px] font-medium text-content-secondary dark:text-dark-content-secondary truncate">
                  {userLabel}
                </span>
                <span
                  className="text-[10px] text-content-tertiary dark:text-dark-content-secondary flex-shrink-0"
                  aria-hidden="true"
                >
                  ·
                </span>
                <time
                  dateTime={entry.changedAt}
                  className="text-[10px] text-content-tertiary dark:text-dark-content-secondary flex-shrink-0"
                >
                  {formatTimestamp(entry.changedAt, formatDate)}
                </time>
              </div>

              {/* Notas opcionales */}
              {entry.notes && (
                <p className="text-[10px] text-content-secondary dark:text-dark-content-secondary italic mt-1 leading-relaxed line-clamp-2">
                  &ldquo;{entry.notes}&rdquo;
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
